import { useState, useEffect } from 'react';
import { User, Plus, Shield, Edit, ChevronRight, ChevronDown, CheckCircle, Circle, ExternalLink, Users, Calendar, ClipboardList, Building, Briefcase, Search, Rocket, Eye, AlertTriangle, Activity, Lock, Layers, Target, MessageSquare } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useReplica } from '../contexts/ReplicaContext';
import { useTranslation } from 'react-i18next';
import { replicaService, workspaceService, employmentService } from '../lib/services';
import { api } from '../lib/api';
import CognitiveDNAPanel from './training/CognitiveDNAPanel';
import ROIIndicator from './ROIIndicator';
import CoachingMotivation from './training/CoachingMotivation';
import CognitiveLab from './cognitive/CognitiveLab';
import SkillLibrary from './cognitive/SkillLibrary';
import IntelligenceTab from './cognitive/IntelligenceTab';

import { UserReplica, ReplicaFormData } from '../types';

interface PersonalDashboardViewProps {
    onNavigate: (view: string) => void;
}

// Dashboard State Types
type DashboardState = 'intent' | 'bond' | 'governance';

// Onboarding step status
interface OnboardingStep {
    id: number;
    key: string;
    completed: boolean;
    action: string;
}

export default function PersonalDashboardView({ onNavigate }: PersonalDashboardViewProps) {
    const { user } = useAuth();
    const { getUserReplicas, deployReplica } = useReplica();
    const { t } = useTranslation('dashboard');
    const [deploying, setDeploying] = useState(false);
    const [loadingBackend, setLoadingBackend] = useState(true);

    // Fetch replicas from backend API (source of truth) with fallback to context
    const contextReplicas = user?.uid ? getUserReplicas(user.uid) : [];
    const [backendReplicas, setBackendReplicas] = useState<UserReplica[]>([]);

    useEffect(() => {
        if (!user?.uid) {
            setLoadingBackend(false);
            return;
        }
        replicaService.getUserReplicas(user.uid).then(res => {
            if (res.data?.replicas && res.data.replicas.length > 0) {
                const mapped: UserReplica[] = res.data.replicas.map((r: any) => ({
                    id: r.id,
                    name: r.name || 'Unnamed Replica',
                    category: r.category || 'General',
                    description: r.description || '',
                    creatorId: r.ownerId || user.uid,
                    status: r.status === 'ACTIVE' || r.status === 'deployed' ? 'deployed' as const : 'draft' as const,
                    createdAt: r.createdAt || new Date().toISOString(),
                    updatedAt: r.updatedAt || new Date().toISOString(),
                    rating: r.rating ?? 0,
                    reviews: r.reviewCount ?? 0,
                    parentId: null,
                    price: { amount: r.priceAmount ?? 0, period: (r.pricePeriod || 'hour') as 'hour' | 'month' },
                    hasHumanGuardian: !!r.guardianAssignment && r.guardianAssignment !== 'no-guardian',
                    image: r.avatarImageUrl || '',
                    skills: r.expertise ? [r.expertise] : [],
                    supportedTasks: [],
                    formData: {
                        name: r.name || '',
                        category: r.category || '',
                        description: r.description || '',
                        avatarStyle: r.avatarStyle || 'realistic',
                        gender: r.avatarGender || 'neutral',
                        ageRange: r.avatarAge || 'adult',
                        ethnicity: r.avatarEthnicity || 'diverse',
                        voiceTone: r.voiceTone || 'professional',
                        voiceSpeed: r.voiceSpeed ?? 1.0,
                        voiceSamples: [],
                        expertise: r.expertise || '',
                        language: r.language || 'English',
                        responseSpeed: r.responseSpeed || 'fast',
                        selectedPersonalityTraits: r.personalityTraits
                            ? (typeof r.personalityTraits === 'string'
                                ? r.personalityTraits.split(', ').filter(Boolean)
                                : Array.isArray(r.personalityTraits) ? r.personalityTraits : [])
                            : [],
                        personalityDescription: r.personality || '',
                        trainingMaterials: [],
                        sampleConversations: '',
                        dosAndDonts: '',
                        boundaries: {
                            noExternalComms: false, noFinancialData: false,
                            noPersonnelDecisions: false, noTransactions: false,
                            noLegalInquiries: false, customBoundaries: [],
                        },
                        guardianConfig: {
                            guardianType: (r.guardianAssignment || 'self') as any,
                            financialThreshold: 1000,
                            externalCommsPolicy: 'draft-only' as const,
                            customEscalationRules: [],
                        },
                        consent: {
                            understandsLimitations: true,
                            acceptsResponsibility: true,
                            confirmedGuardianSetup: true,
                        },
                        deploymentOption: (r.deploymentOption || 'website-widget') as any,
                        guardianAssignment: (r.guardianAssignment || 'self') as any,
                    } as ReplicaFormData,
                }));
                setBackendReplicas(mapped);
            }
        }).catch(err => {
            console.warn('Failed to fetch replicas from backend:', err);
        }).finally(() => {
            setLoadingBackend(false);
        });
    }, [user?.uid]);

    // Merge: backend replicas take priority, then add context-only replicas
    const backendIds = new Set(backendReplicas.map(r => r.id));
    const userReplicas = [
        ...backendReplicas,
        ...contextReplicas.filter(r => !backendIds.has(r.id)),
    ];
    const draftReplicas = userReplicas.filter(r => r.status === 'draft');
    const deployedReplicas = userReplicas.filter(r => r.status === 'deployed');

    // Determine dashboard state
    const getDashboardState = (): DashboardState => {
        if (userReplicas.length === 0) return 'intent';
        if (deployedReplicas.length === 0) return 'bond';
        return 'governance';
    };

    const dashboardState = getDashboardState();
    const primaryReplica = userReplicas[0];

    // ═══════════════════════════════════════════════════════════════════════════
    // API DATA: Stats, Onboarding, and Employments
    // ═══════════════════════════════════════════════════════════════════════════
    const [apiStats, setApiStats] = useState<{
        representationsMade: number;
        valueReturned: number;
        performanceScore: number;
    } | null>(null);
    const [apiOnboarding, setApiOnboarding] = useState<{
        hasReplica: boolean;
        hasTrainingData: boolean;
        hasReviewedGovernance: boolean;
        isDeployed: boolean;
    } | null>(null);
    const [employments, setEmployments] = useState<Array<{
        id: string;
        workspaceId: string;
        workspaceName: string;
        companyName: string | null;
        status: string;
        autonomyLevel: number;
        teamCount: number;
        assignedAt: string;
    }>>([]);

    useEffect(() => {
        workspaceService.getStats().then(res => {
            if (res.data) {
                setApiStats(res.data.stats);
                setApiOnboarding(res.data.onboarding);
            }
        }).catch(() => { });

        employmentService.list().then(res => {
            if (res.data) {
                setEmployments(res.data.employments);
            }
        }).catch(() => { });
    }, []);

    // Load real sovereignty status on mount
    useEffect(() => {
        if (!primaryReplica?.id) return;
        api.get<{ success: boolean; data: { operationalStatus: string } }>(`/sovereignty/${primaryReplica.id}/status`)
            .then(res => {
                if (res.data?.data?.operationalStatus) {
                    setOperationalStatus(res.data.data.operationalStatus);
                }
            })
            .catch(() => { });
    }, [primaryReplica?.id]);

    // Calculate onboarding progress
    const getOnboardingSteps = (): OnboardingStep[] => {
        const hasReplica = apiOnboarding?.hasReplica ?? userReplicas.length > 0;
        const hasTrainingData = apiOnboarding?.hasTrainingData ?? false;
        const hasReviewedGovernance = apiOnboarding?.hasReviewedGovernance ?? false;
        const isActivated = apiOnboarding?.isDeployed ?? deployedReplicas.length > 0;

        return [
            { id: 1, key: 'step_1', completed: hasReplica, action: 'create-replica' },
            { id: 2, key: 'step_2', completed: hasTrainingData, action: 'data' },
            { id: 3, key: 'step_3', completed: hasReviewedGovernance, action: 'governance' },
            { id: 4, key: 'step_4', completed: isActivated, action: 'deploy' },
        ];
    };

    const onboardingSteps = getOnboardingSteps();
    const completedSteps = onboardingSteps.filter(s => s.completed).length;
    const isOnboardingComplete = completedSteps === onboardingSteps.length;

    // ═════════════════════════════════════════════════════════════════════
    // SOVEREIGNTY STATE
    // ═════════════════════════════════════════════════════════════════════
    const [operationalStatus, setOperationalStatus] = useState<string>('ACTIVE');
    const [showKillConfirm, setShowKillConfirm] = useState(false);
    const [killReason, setKillReason] = useState('');
    const [killConfirmText, setKillConfirmText] = useState('');
    const [killCooldown, setKillCooldown] = useState(0);
    const [statusLoading, setStatusLoading] = useState(false);
    const [sovereigntyError, setSovereigntyError] = useState<string | null>(null);

    // Dashboard Tabs
    const [activeTab, setActiveTab] = useState<'overview' | 'cognitive-lab' | 'skills' | 'intelligence'>('overview');

    // Progressive disclosure states
    const [healthExpanded, setHealthExpanded] = useState(false);
    const [governanceExpanded, setGovernanceExpanded] = useState(false);

    // Autonomy mode (UI state — would be persisted via API in production)
    const [autonomyMode, setAutonomyMode] = useState<'manual' | 'assisted' | 'autonomous'>('assisted');

    const statusColors: Record<string, { bg: string; text: string; ring: string; label: string; desc: string }> = {
        ACTIVE: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', ring: 'ring-emerald-500/30', label: 'Operational', desc: 'Replica is active and representing under governance' },
        SUSPENDED: { bg: 'bg-amber-500/15', text: 'text-amber-400', ring: 'ring-amber-500/30', label: 'Suspended', desc: 'All operations paused — governance preserved' },
        KILLED: { bg: 'bg-red-500/15', text: 'text-red-400', ring: 'ring-red-500/30', label: 'Killed — Terminal', desc: 'Permanently halted — audit trail preserved' },
        FROZEN: { bg: 'bg-blue-500/15', text: 'text-blue-400', ring: 'ring-blue-500/30', label: 'Frozen', desc: 'Regulatory hold — awaiting compliance review' },
    };

    const currentStatusStyle = statusColors[operationalStatus] || statusColors.ACTIVE;

    const handleSuspendResume = async () => {
        if (!primaryReplica) {
            setSovereigntyError('No replica found');
            return;
        }
        setSovereigntyError(null);
        setStatusLoading(true);

        const previousStatus = operationalStatus;
        const newStatus = operationalStatus === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
        setOperationalStatus(newStatus);

        try {
            const endpoint = previousStatus === 'ACTIVE' ? 'suspend' : 'resume';
            const res = await api.post<{ success: boolean; data: { newStatus?: string } }>(
                `/sovereignty/${primaryReplica.id}/${endpoint}`,
                { reason: `Owner ${endpoint} from dashboard` }
            );
            if (res.data?.success) {
                setOperationalStatus(res.data.data?.newStatus || newStatus);
            } else if (res.error) {
                console.warn('Sovereignty backend error (UI state kept):', res.error);
            }
        } catch (e) {
            console.warn('Sovereignty action network error (UI state kept):', e);
        }
        setStatusLoading(false);
    };

    const handleKillConfirm = async () => {
        if (!primaryReplica) {
            setSovereigntyError('No replica found');
            return;
        }
        setSovereigntyError(null);
        setStatusLoading(true);

        setOperationalStatus('KILLED');
        setShowKillConfirm(false);

        try {
            const res = await api.post<{ success: boolean }>(
                `/sovereignty/${primaryReplica.id}/kill`,
                { reason: killReason || 'Owner-initiated kill switch from dashboard' }
            );
            if (res.data?.success) {
                // Confirmed by server
            } else if (res.error) {
                console.warn('Kill switch backend error (UI state kept):', res.error);
            }
        } catch (e) {
            console.warn('Kill switch network error (UI state kept):', e);
        }
        setStatusLoading(false);
    };

    // ═════════════════════════════════════════════════════════════════════
    // Loading State — wait for backend + context before showing empty state
    // ═════════════════════════════════════════════════════════════════════
    if (loadingBackend && userReplicas.length === 0) {
        return (
            <div className="h-full flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                    <div className="w-10 h-10 border-3 border-hig-accent/30 border-t-hig-accent rounded-full animate-spin" />
                    <p className="text-sm text-hig-text-tertiary">{t('loading.replicas', 'Loading your Replica...')}</p>
                </div>
            </div>
        );
    }

    // ═════════════════════════════════════════════════════════════════════
    // No-Replica "Intent" State — full CTA
    // ═════════════════════════════════════════════════════════════════════
    if (dashboardState === 'intent') {
        return (
            <div className="h-full flex flex-col gap-4 max-w-6xl mx-auto">
                {/* Hero CTA */}
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center max-w-xl">
                        <div className="w-20 h-20 rounded-3xl bg-hig-accent/10 flex items-center justify-center mx-auto mb-6">
                            <User size={36} className="text-hig-accent" />
                        </div>
                        <h1 className="text-3xl lg:text-4xl font-bold text-hig-text-primary mb-3 tracking-tight leading-tight">
                            {t('hero.no_replica_headline')}
                        </h1>
                        <p className="text-base text-hig-text-secondary mb-8 leading-relaxed max-w-md mx-auto">
                            {t('hero.no_replica_explainer')}
                        </p>
                        <div className="flex flex-wrap items-center justify-center gap-4">
                            <button
                                onClick={() => onNavigate('create-replica')}
                                data-tutorial="create-replica"
                                className="btn-primary text-base px-10 py-4 shadow-lg shadow-hig-accent/25 rounded-2xl font-semibold tracking-wide"
                            >
                                <Plus size={18} className="mr-2" />
                                {t('hero.create_cta')}
                            </button>
                            <button
                                onClick={() => onNavigate('home')}
                                className="flex items-center gap-2 text-hig-text-secondary hover:text-hig-accent transition-colors px-4 py-3 rounded-xl hover:bg-hig-accent/5"
                            >
                                <ExternalLink size={16} />
                                {t('hero.learn_more_cta')}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Quick links row */}
                <div className="grid grid-cols-3 gap-3">
                    <button onClick={() => onNavigate('replica-directory')} className="card-interactive p-4 text-left group">
                        <div className="flex items-center gap-3">
                            <Search size={18} className="text-indigo-400" />
                            <span className="text-sm font-medium text-hig-text-primary">Replica Directory</span>
                            <ChevronRight size={14} className="text-hig-text-tertiary ml-auto group-hover:translate-x-1 transition-transform" />
                        </div>
                    </button>
                    <button onClick={() => onNavigate('work')} className="card-interactive p-4 text-left group">
                        <div className="flex items-center gap-3">
                            <ClipboardList size={18} className="text-blue-400" />
                            <span className="text-sm font-medium text-hig-text-primary">Mandates</span>
                            <ChevronRight size={14} className="text-hig-text-tertiary ml-auto group-hover:translate-x-1 transition-transform" />
                        </div>
                    </button>
                    <button onClick={() => onNavigate('home')} className="card-interactive p-4 text-left group">
                        <div className="flex items-center gap-3">
                            <ExternalLink size={18} className="text-hig-accent" />
                            <span className="text-sm font-medium text-hig-text-primary">Learn More</span>
                            <ChevronRight size={14} className="text-hig-text-tertiary ml-auto group-hover:translate-x-1 transition-transform" />
                        </div>
                    </button>
                </div>
            </div>
        );
    }

    // ═════════════════════════════════════════════════════════════════════
    // GOVERN MODE — Governance Command Center
    // ═════════════════════════════════════════════════════════════════════
    const isKilled = operationalStatus === 'KILLED';

    return (
        <div className={`h-full flex flex-col gap-3 max-w-6xl mx-auto ${isKilled ? 'killed-state' : ''}`}>

            {/* Dashboard Navigation Tabs */}
            <div className={`flex border-b ${isKilled ? 'border-red-500/20' : 'border-hig-divider'} mb-2`}>
                <button
                    onClick={() => setActiveTab('overview')}
                    className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === 'overview'
                            ? `border-hig-accent ${isKilled ? 'text-red-400' : 'text-hig-text-primary'}`
                            : `border-transparent ${isKilled ? 'text-red-400/50 hover:text-red-300' : 'text-hig-text-secondary hover:text-hig-text-primary'}`
                    }`}
                >
                    Overview & Governance
                </button>
                <button
                    onClick={() => setActiveTab('cognitive-lab')}
                    disabled={isKilled}
                    className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === 'cognitive-lab'
                            ? 'border-hig-accent text-hig-text-primary'
                            : 'border-transparent text-hig-text-secondary hover:text-hig-text-primary'
                    } disabled:opacity-30 disabled:cursor-not-allowed`}
                >
                    Cognitive Lab
                </button>
                <button
                    onClick={() => setActiveTab('skills')}
                    disabled={isKilled}
                    className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === 'skills'
                            ? 'border-hig-accent text-hig-text-primary'
                            : 'border-transparent text-hig-text-secondary hover:text-hig-text-primary'
                    } disabled:opacity-30 disabled:cursor-not-allowed`}
                >
                    Skill Library
                </button>
                <button
                    onClick={() => setActiveTab('intelligence')}
                    disabled={isKilled}
                    className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === 'intelligence'
                            ? 'border-hig-accent text-hig-text-primary'
                            : 'border-transparent text-hig-text-secondary hover:text-hig-text-primary'
                    } disabled:opacity-30 disabled:cursor-not-allowed`}
                >
                    Intelligence
                </button>

            </div>


            {/* ─── TAB CONTENT: OVERVIEW ─── */}
            {activeTab === 'overview' && (
                <div className="flex flex-col gap-5 animate-fade-in">

                    {/* ═══ KILLED STATE ═══ */}
                    {isKilled && primaryReplica && (
                        <div className="card border border-red-500/30 bg-red-950/10 p-5">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-10 h-10 rounded-xl bg-red-600/20 flex items-center justify-center">
                                    <AlertTriangle size={20} className="text-red-400" />
                                </div>
                                <div>
                                    <h3 className="font-bold text-red-400 text-base">Replica Terminated</h3>
                                    <p className="text-xs text-red-400/70">This action was permanent and irreversible</p>
                                </div>
                            </div>
                            <p className="text-sm text-hig-text-secondary leading-relaxed">
                                <strong className="text-hig-text-primary">{primaryReplica.name}</strong> has been permanently terminated.
                                All operations halted. A signed audit record has been created.
                            </p>
                        </div>
                    )}

                    {/* ═══ SECTION ① — GREETING + REPLICA IDENTITY ═══ */}
                    {primaryReplica && (
                        <div className="card p-5 relative overflow-hidden">
                            {/* Clean card — no decorative shimmer */}

                            {/* Greeting */}
                            <p className="text-[13px] text-hig-text-tertiary mb-4">
                                {(() => {
                                    const h = new Date().getHours();
                                    const greeting = h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
                                    const name = user?.displayName?.split(' ')[0] || '';
                                    return name ? `${greeting}, ${name}` : greeting;
                                })()}
                            </p>

                            <div className="flex items-center justify-between relative">
                                <div className="flex items-center gap-4">
                                    {/* Living Avatar with pulse ring */}
                                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-xl font-bold ${
                                        isKilled
                                            ? 'bg-gradient-to-br from-red-500/30 to-red-700/30 text-red-300 avatar-alive-red'
                                            : operationalStatus === 'SUSPENDED'
                                                ? 'bg-gradient-to-br from-amber-500/30 to-amber-700/30 text-amber-300 avatar-alive-amber'
                                                : 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/15 avatar-alive'
                                    }`}>
                                        {primaryReplica.name.charAt(0)}
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2.5 mb-1">
                                            <h2 className="text-lg font-bold text-hig-text-primary">{primaryReplica.name}</h2>
                                            <div className={`flex items-center gap-1.5 px-2 py-0.5 rounded-full ${currentStatusStyle.bg} ring-1 ${currentStatusStyle.ring}`}>
                                                <div className={`w-2 h-2 rounded-full ${currentStatusStyle.text.replace('text-', 'bg-')} ${operationalStatus === 'ACTIVE' ? 'animate-pulse' : ''}`} />
                                                <span className={`text-[10px] font-bold uppercase tracking-wider ${currentStatusStyle.text}`}>{currentStatusStyle.label}</span>
                                            </div>
                                        </div>
                                        {/* Natural language status line */}
                                        <p className="text-xs text-hig-text-tertiary leading-relaxed max-w-sm">
                                            {isKilled
                                                ? 'Operations permanently halted. Audit record preserved.'
                                                : operationalStatus === 'SUSPENDED'
                                                    ? 'All activity paused. Governance remains intact.'
                                                    : `${apiStats?.representationsMade ?? 0} representations made · ${apiStats?.performanceScore ?? 0}% accuracy · Guardian active`
                                            }
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    {!isKilled && operationalStatus !== 'KILLED' && (
                                        <button onClick={handleSuspendResume} disabled={statusLoading}
                                            className={`px-3.5 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-50 ${
                                                operationalStatus === 'SUSPENDED'
                                                    ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20'
                                                    : 'bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20'
                                            }`}>
                                            {statusLoading ? '...' : operationalStatus === 'SUSPENDED' ? 'Resume' : 'Suspend'}
                                        </button>
                                    )}
                                    <button onClick={() => onNavigate('my-replica')}
                                        className="px-3.5 py-1.5 rounded-lg text-xs font-medium text-hig-text-secondary hover:text-hig-text-primary bg-hig-bg-tertiary/50 hover:bg-hig-bg-tertiary transition-colors flex items-center gap-1.5">
                                        Manage <ChevronRight size={12} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ═══ EVOLUTION STAGE INDICATOR: Learn → Replicate → Apex ═══ */}
                    {primaryReplica && !isKilled && (() => {
                        // Determine current stage from replica data
                        const hasTraining = apiOnboarding?.hasTrainingData ?? false;
                        const isDeployed = apiOnboarding?.isDeployed ?? deployedReplicas.length > 0;
                        const perfScore = apiStats?.performanceScore ?? 0;
                        const currentStage: 'learn' | 'replicate' | 'apex' =
                            isDeployed && perfScore >= 80 ? 'apex' :
                            isDeployed || hasTraining ? 'replicate' :
                            'learn';
                        const stageIndex = currentStage === 'learn' ? 0 : currentStage === 'replicate' ? 1 : 2;

                        const stages = [
                            { key: 'learn', icon: '🧬', label: 'Learn', desc: 'Absorbing your style, strengths & gaps', color: 'sky' },
                            { key: 'replicate', icon: '🔄', label: 'Replicate', desc: 'Working alongside you, filling your gaps', color: 'violet' },
                            { key: 'apex', icon: '⚡', label: 'Apex', desc: 'Autonomous, hireable, fully evolved', color: 'amber' },
                        ];

                        return (
                            <div className="card p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <p className="text-xs font-semibold text-hig-text-secondary tracking-wide uppercase">Evolution Stage</p>
                                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                                        currentStage === 'apex' ? 'bg-amber-500/15 text-amber-400' :
                                        currentStage === 'replicate' ? 'bg-violet-500/15 text-violet-400' :
                                        'bg-sky-500/15 text-sky-400'
                                    }`}>{stages[stageIndex].label}</span>
                                </div>
                                <div className="flex items-center gap-0">
                                    {stages.map((stage, i) => {
                                        const isActive = i <= stageIndex;
                                        const isCurrent = i === stageIndex;
                                        const colorMap: Record<string, { activeBg: string; activeText: string; ring: string }> = {
                                            sky:    { activeBg: 'bg-sky-500/15',    activeText: 'text-sky-400',    ring: 'ring-sky-500/30' },
                                            violet: { activeBg: 'bg-violet-500/15', activeText: 'text-violet-400', ring: 'ring-violet-500/30' },
                                            amber:  { activeBg: 'bg-amber-500/15',  activeText: 'text-amber-400',  ring: 'ring-amber-500/30' },
                                        };
                                        const c = colorMap[stage.color];
                                        return (
                                            <div key={stage.key} className="flex items-center flex-1">
                                                <div className={`flex flex-col items-center flex-1 ${isCurrent ? 'opacity-100' : isActive ? 'opacity-80' : 'opacity-30'}`}>
                                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-base mb-1.5 transition-all duration-300 ${
                                                        isActive ? `${c.activeBg} ring-1 ${c.ring}` : 'bg-hig-bg-tertiary/50'
                                                    } ${isCurrent ? 'scale-110 shadow-lg' : ''}`}>
                                                        {stage.icon}
                                                    </div>
                                                    <span className={`text-[11px] font-bold ${isActive ? c.activeText : 'text-hig-text-tertiary'}`}>{stage.label}</span>
                                                    <span className="text-[9px] text-hig-text-tertiary text-center mt-0.5 max-w-[100px] leading-tight">{stage.desc}</span>
                                                </div>
                                                {i < stages.length - 1 && (
                                                    <div className="flex-shrink-0 w-8 flex items-center justify-center -mt-6">
                                                        <div className={`h-0.5 w-full rounded-full transition-all duration-500 ${
                                                            i < stageIndex ? 'bg-gradient-to-r from-sky-400 to-violet-400' : 'bg-hig-bg-tertiary/30'
                                                        }`} />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })()}

                    {/* ═══ ONBOARDING BAR ═══ */}
                    {!isOnboardingComplete && !isKilled && (
                        <div className="card p-4 border border-hig-accent/20 bg-gradient-to-r from-hig-accent/[0.03] to-transparent">
                            <div className="flex items-center justify-between mb-3">
                                <p className="text-sm font-semibold text-hig-text-primary">Getting Started</p>
                                <span className="text-xs text-hig-accent font-medium">{completedSteps}/{onboardingSteps.length} steps</span>
                            </div>
                            <div className="w-full bg-hig-bg-tertiary rounded-full h-1.5 mb-3">
                                <div className="bg-hig-accent h-1.5 rounded-full transition-all duration-500"
                                    style={{ width: `${onboardingSteps.length > 0 ? (completedSteps / onboardingSteps.length) * 100 : 0}%` }} />
                            </div>
                            <div className="flex gap-2 flex-wrap">
                                {onboardingSteps.map((step) => (
                                    <button key={step.id} onClick={() => step.action && onNavigate(step.action)} disabled={step.completed}
                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                            step.completed ? 'text-hig-text-tertiary line-through opacity-50' : 'text-hig-accent bg-hig-accent/10 hover:bg-hig-accent/20'
                                        }`}>
                                        {step.completed ? <CheckCircle size={12} /> : <Circle size={12} />}
                                        {t(`onboarding.${step.key}`, step.key)}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ═══ SECTION ② — TODAY'S FOCUS (or All Good) ═══ */}
                    {!isKilled && (
                        isOnboardingComplete && (apiStats?.performanceScore ?? 0) >= 80 ? (
                            /* Empty state delight — all good */
                            <div className="card p-6 flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-400 check-animated">
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-hig-text-primary mb-0.5">All good for now</p>
                                    <p className="text-xs text-hig-text-tertiary">Your Replica is on track. No escalations pending.</p>
                                </div>
                            </div>
                        ) : (
                            /* Action strip */
                            <div className="grid grid-cols-4 gap-3">
                                <button onClick={() => onNavigate(primaryReplica ? `training-${primaryReplica.id}` : 'training-rounds')}
                                    className="card-interactive p-4 flex flex-col items-center text-center group">
                                    <div className="w-10 h-10 rounded-xl bg-hig-accent/10 flex items-center justify-center mb-2.5 group-hover:scale-110 transition-transform duration-200">
                                        <Target size={20} className="text-hig-accent" />
                                    </div>
                                    <p className="text-sm font-semibold text-hig-text-primary mb-0.5">Train</p>
                                    <p className="text-[11px] text-hig-text-tertiary">2-min daily round</p>
                                </button>
                                <button onClick={() => onNavigate('work')}
                                    className="card-interactive p-4 flex flex-col items-center text-center group">
                                    <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center mb-2.5 group-hover:scale-110 transition-transform duration-200">
                                        <ClipboardList size={20} className="text-amber-400" />
                                    </div>
                                    <p className="text-sm font-semibold text-hig-text-primary mb-0.5">Mandates</p>
                                    <p className="text-[11px] text-hig-text-tertiary">Deploy mode</p>
                                </button>
                                <button onClick={() => primaryReplica && onNavigate(`chat-${primaryReplica.id}`)} disabled={!primaryReplica}
                                    className="card-interactive p-4 flex flex-col items-center text-center group disabled:opacity-40">
                                    <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center mb-2.5 group-hover:scale-110 transition-transform duration-200">
                                        <MessageSquare size={20} className="text-purple-400" />
                                    </div>
                                    <p className="text-sm font-semibold text-hig-text-primary mb-0.5">Chat</p>
                                    <p className="text-[11px] text-hig-text-tertiary">with your Replica</p>
                                </button>
                                <button onClick={() => onNavigate('cognitive-merge')}
                                    className="card-interactive p-4 flex flex-col items-center text-center group relative">
                                    <div className="absolute top-2 right-2 text-[8px] px-1.5 py-0.5 rounded-full bg-gradient-to-r from-purple-500/20 to-cyan-500/20 text-purple-400 border border-purple-500/20 font-bold uppercase tracking-wider">
                                        Premium
                                    </div>
                                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/10 to-cyan-500/10 flex items-center justify-center mb-2.5 group-hover:scale-110 transition-transform duration-200">
                                        <Layers size={20} className="text-purple-400" />
                                    </div>
                                    <p className="text-sm font-semibold text-hig-text-primary mb-0.5">Merge</p>
                                    <p className="text-[11px] text-hig-text-tertiary">Cognitive Fusion</p>
                                </button>
                            </div>
                        )
                    )}

                    {/* ═══ SECTION ③ — HEALTH SNAPSHOT ═══ */}
                    <div className="card overflow-hidden">
                        <button onClick={() => setHealthExpanded(!healthExpanded)}
                            className="w-full p-4 flex items-center justify-between hover:bg-hig-bg-elevated/30 transition-colors">
                            <div className="flex items-center gap-2">
                                <Activity size={15} className="text-hig-accent" />
                                <p className="text-sm font-semibold text-hig-text-primary">Health</p>
                            </div>
                            <div className="flex items-center gap-3">
                                <span className="text-xs text-hig-text-tertiary">{apiStats?.performanceScore ?? 0}% accuracy</span>
                                <ChevronDown size={14} className={`text-hig-text-tertiary transition-transform duration-300 ${healthExpanded ? 'rotate-180' : ''}`} />
                            </div>
                        </button>
                        <div className="px-4 pb-4 space-y-3">
                            <div>
                                <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-xs text-hig-text-secondary">Accuracy</span>
                                    <span className="text-xs font-semibold text-hig-text-primary">{apiStats?.performanceScore ?? 0}%</span>
                                </div>
                                <div className="w-full bg-hig-bg-tertiary rounded-full h-1.5 overflow-hidden">
                                    <div className="bg-gradient-to-r from-hig-accent to-blue-400 h-1.5 rounded-full transition-all duration-1000 ease-out" style={{ width: `${apiStats?.performanceScore ?? 0}%` }} />
                                </div>
                            </div>
                            <div>
                                <div className="flex items-center justify-between mb-1.5">
                                    <span className="text-xs text-hig-text-secondary">Safety</span>
                                    <span className="text-xs font-semibold text-emerald-400">100%</span>
                                </div>
                                <div className="w-full bg-hig-bg-tertiary rounded-full h-1.5 overflow-hidden">
                                    <div className="bg-gradient-to-r from-emerald-500 to-emerald-400 h-1.5 rounded-full w-full" />
                                </div>
                            </div>
                        </div>
                        {/* Smooth expand drawer */}
                        <div className={`collapsible-drawer ${healthExpanded ? 'open' : ''}`}>
                            <div className="drawer-content">
                                <div className="border-t border-hig-divider">
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 p-4 drawer-stagger">
                                        <CognitiveDNAPanel compact replicaId={primaryReplica?.id || ''} />
                                        <CoachingMotivation compact />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ═══ SECTION ④ — GOVERNANCE DRAWER ═══ */}
                    <div className="card overflow-hidden">
                        <button onClick={() => setGovernanceExpanded(!governanceExpanded)}
                            className="w-full p-4 flex items-center justify-between hover:bg-hig-bg-elevated/30 transition-colors">
                            <div className="flex items-center gap-2.5">
                                <Shield size={16} className={isKilled ? 'text-red-400' : 'text-emerald-400'} />
                                <p className="text-sm font-semibold text-hig-text-primary">Governance</p>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className="flex items-center gap-2 text-xs text-hig-text-tertiary">
                                    <span>Guardian: {isKilled ? 'N/A' : 'Active'}</span>
                                    <span className="opacity-30">|</span>
                                    <span>Memory: 3 layers</span>
                                    <span className="opacity-30">|</span>
                                    <span>Overlays: {employments.length}</span>
                                </div>
                                <ChevronDown size={14} className={`text-hig-text-tertiary transition-transform duration-300 ${governanceExpanded ? 'rotate-180' : ''}`} />
                            </div>
                        </button>
                        {/* Smooth expand drawer */}
                        <div className={`collapsible-drawer ${governanceExpanded ? 'open' : ''}`}>
                            <div className="drawer-content">
                                <div className="border-t border-hig-divider p-4 space-y-4 drawer-stagger">
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                        <div className="bg-hig-bg-tertiary/50 rounded-xl p-3.5 hover:bg-hig-bg-tertiary/70 transition-colors">
                                            <p className="text-[11px] text-hig-text-tertiary uppercase tracking-wider mb-2">Autonomy</p>
                                            <div className="flex gap-1">
                                                {(['manual', 'assisted', 'autonomous'] as const).map(mode => (
                                                    <button key={mode} onClick={() => !isKilled && setAutonomyMode(mode)} disabled={isKilled}
                                                        className={`flex-1 px-2 py-1 rounded-md text-[10px] font-medium transition-all duration-200 ${autonomyMode === mode ? 'bg-hig-accent text-[#0F172A] shadow-sm shadow-hig-accent/20' : 'text-hig-text-tertiary hover:text-hig-text-secondary'}`}>
                                                        {mode.charAt(0).toUpperCase() + mode.slice(1)}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="bg-hig-bg-tertiary/50 rounded-xl p-3.5 hover:bg-hig-bg-tertiary/70 transition-colors">
                                            <p className="text-[11px] text-hig-text-tertiary uppercase tracking-wider mb-2">Decision Guardian™</p>
                                            <div className="flex items-center gap-1.5">
                                                <CheckCircle size={14} className="text-emerald-400" />
                                                <span className="text-sm font-medium text-emerald-400">{isKilled ? 'Disabled' : 'Active'}</span>
                                            </div>
                                        </div>
                                        <div className="bg-hig-bg-tertiary/50 rounded-xl p-3.5 hover:bg-hig-bg-tertiary/70 transition-colors">
                                            <p className="text-[11px] text-hig-text-tertiary uppercase tracking-wider mb-2">Memory</p>
                                            <p className="text-sm font-semibold text-hig-text-primary">3 Layers</p>
                                            <p className="text-[10px] text-hig-text-tertiary mt-0.5">Short · Long · Skill</p>
                                        </div>
                                        <div className="bg-hig-bg-tertiary/50 rounded-xl p-3.5 hover:bg-hig-bg-tertiary/70 transition-colors">
                                            <p className="text-[11px] text-hig-text-tertiary uppercase tracking-wider mb-2">Replicas</p>
                                            <p className="text-sm font-semibold text-hig-text-primary">{userReplicas.length}</p>
                                        </div>
                                        <div className="bg-hig-bg-tertiary/50 rounded-xl p-3.5 hover:bg-hig-bg-tertiary/70 transition-colors">
                                            <p className="text-[11px] text-hig-text-tertiary uppercase tracking-wider mb-2">Overlays</p>
                                            <p className="text-sm font-semibold text-hig-text-primary">{employments.length}</p>
                                        </div>
                                        <div className="bg-hig-bg-tertiary/50 rounded-xl p-3.5 hover:bg-hig-bg-tertiary/70 transition-colors">
                                            <p className="text-[11px] text-hig-text-tertiary uppercase tracking-wider mb-2">ROI</p>
                                            <p className="text-sm font-semibold text-hig-accent">{apiStats?.performanceScore ?? 0}%</p>
                                            <p className="text-[10px] text-hig-text-tertiary mt-0.5">Performance score</p>
                                        </div>
                                    </div>
                                    <div className="bg-hig-bg-tertiary/30 rounded-xl p-4">
                                        <p className="text-xs font-semibold text-hig-text-secondary uppercase tracking-wider mb-3">Risk & Compliance</p>
                                        <div className="space-y-2.5">
                                            <div className="flex items-center justify-between"><span className="text-xs text-hig-text-secondary">Override Rate</span><span className="text-xs font-medium text-emerald-400">0%</span></div>
                                            <div className="flex items-center justify-between"><span className="text-xs text-hig-text-secondary">Drift Detection</span><span className="text-xs font-medium text-emerald-400">None</span></div>
                                            <div className="flex items-center justify-between"><span className="text-xs text-hig-text-secondary">Scope Compliance</span><span className="text-xs font-medium text-emerald-400">100%</span></div>
                                        </div>
                                    </div>
                                    {employments.length > 0 && (
                                        <div>
                                            <p className="text-xs font-semibold text-hig-text-secondary uppercase tracking-wider mb-2">Active Overlays</p>
                                            <div className="space-y-2">
                                                {employments.slice(0, 3).map((emp: any) => (
                                                    <button key={emp.id} onClick={() => onNavigate(`employment-${emp.id}`)}
                                                        className="w-full flex items-center gap-3 p-3 rounded-xl bg-hig-bg-tertiary/30 hover:bg-hig-bg-tertiary/50 transition-all text-left group">
                                                        <Building size={14} className="text-hig-text-tertiary group-hover:text-hig-text-secondary transition-colors" />
                                                        <div className="min-w-0 flex-1">
                                                            <p className="text-sm font-medium text-hig-text-primary truncate">{emp.companyName || 'Company'}</p>
                                                            <p className="text-[11px] text-hig-text-tertiary capitalize">{emp.status || 'active'}</p>
                                                        </div>
                                                        <ChevronRight size={12} className="text-hig-text-tertiary opacity-0 group-hover:opacity-100 transition-opacity" />
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {!isKilled && (
                                        <div className="pt-2 border-t border-hig-divider">
                                            <button onClick={() => setShowKillConfirm(true)}
                                                className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium text-hig-text-tertiary hover:text-red-400 hover:bg-red-500/10 transition-all opacity-40 hover:opacity-100">
                                                <AlertTriangle size={12} /> Kill Replica (Permanent)
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ═══ KILL CONFIRMATION MODAL ═══ */}
                    {showKillConfirm && primaryReplica && (
                        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setShowKillConfirm(false)}>
                            <div className="w-full max-w-md bg-hig-bg-secondary rounded-2xl border border-red-500/30 shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
                                <div className="p-6 border-b border-red-500/20 bg-red-950/20">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 rounded-xl bg-red-600/20 flex items-center justify-center"><AlertTriangle size={24} className="text-red-400" /></div>
                                        <div><h3 className="font-bold text-red-400 text-lg">Kill Replica</h3><p className="text-sm text-red-400/70">This action is irreversible</p></div>
                                    </div>
                                </div>
                                <div className="p-6">
                                    <p className="text-sm text-hig-text-secondary mb-4 leading-relaxed">
                                        You are about to permanently terminate <strong className="text-hig-text-primary">{primaryReplica.name}</strong>.
                                        This will halt ALL operations and create a signed audit record.
                                    </p>
                                    <input type="text" placeholder="Reason for termination (optional)" value={killReason} onChange={e => setKillReason(e.target.value)}
                                        className="w-full bg-red-950/30 border border-red-500/30 rounded-lg px-3 py-2.5 text-sm text-hig-text-primary placeholder-red-500/40 mb-3 focus:outline-none focus:ring-1 focus:ring-red-500/50" />
                                    <p className="text-xs text-red-400/60 mb-3">Type <strong className="text-red-400">KILL</strong> to confirm:</p>
                                    <input type="text" value={killConfirmText} onChange={e => setKillConfirmText(e.target.value)}
                                        className="w-full bg-red-950/30 border border-red-500/30 rounded-lg px-3 py-2.5 text-sm text-hig-text-primary mb-4 focus:outline-none focus:ring-1 focus:ring-red-500/50" />
                                    <div className="flex items-center gap-3 justify-end">
                                        <button onClick={() => setShowKillConfirm(false)} className="px-5 py-2.5 rounded-lg text-sm text-hig-text-secondary hover:text-hig-text-primary transition-colors">Cancel</button>
                                        <button onClick={() => handleKillReplica(primaryReplica.id)} disabled={killConfirmText !== 'KILL' || killCooldown > 0 || statusLoading}
                                            className="px-6 py-2.5 rounded-lg text-sm font-bold bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50">
                                            {killCooldown > 0 ? `Wait ${killCooldown}s` : statusLoading ? 'Terminating...' : 'Confirm Kill'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            )}
            {activeTab === 'cognitive-lab' && !isKilled && primaryReplica && (
                <div className="flex-1 min-h-[600px] animate-fade-in bg-hig-bg-elevated/30 rounded-2xl border border-hig-divider overflow-hidden relative">
                    <CognitiveLab replicaId={primaryReplica.id} onBack={() => setActiveTab('overview')} />
                </div>
            )}

            {/* ─── TAB CONTENT: SKILL LIBRARY ─── */}
            {activeTab === 'skills' && !isKilled && primaryReplica && (
                <div className="flex-1 min-h-[600px] animate-fade-in bg-hig-bg-elevated/30 rounded-2xl border border-hig-divider overflow-hidden relative">
                    <SkillLibrary replicaId={primaryReplica.id} onBack={() => setActiveTab('overview')} />
                </div>
            )}

            {/* ─── TAB CONTENT: INTELLIGENCE ─── */}
            {activeTab === 'intelligence' && !isKilled && primaryReplica && (
                <div className="flex-1 min-h-[600px] animate-fade-in bg-hig-bg-elevated/30 rounded-2xl border border-hig-divider overflow-hidden relative">
                    <IntelligenceTab replicaId={primaryReplica.id} onBack={() => setActiveTab('overview')} />
                </div>
            )}



            {/* Handle Edge Cases where tabs are clicked but no replica exists */}
            {(activeTab === 'cognitive-lab' || activeTab === 'skills' || activeTab === 'intelligence') && !primaryReplica && (
                 <div className="flex-1 flex flex-col items-center justify-center p-12 text-center bg-hig-bg-elevated/30 rounded-2xl border border-hig-divider">
                     <AlertTriangle size={48} className="text-hig-warning mb-4 opacity-50" />
                     <h3 className="text-xl font-bold text-hig-text-primary mb-2">No Replica Found</h3>
                     <p className="text-hig-text-secondary mb-6">You must create a Replica before accessing these features.</p>
                     <button onClick={() => onNavigate('create-replica')} className="btn-primary px-6 py-2.5 rounded-xl">
                         Create Replica
                     </button>
                 </div>
            )}
        </div>
    );
}
